import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ADMINS_DE_DOMINIO,
  deleteUserInOrganization,
  deleteUsersInRole,
  getAllOrganizations,
  getAllPaises,
  getCognitoUsers,
  getDynamoUsers,
  getUserById,
  getUsersByOrgId,
  getUsersInRole,
  getUsersInSuborg,
  getUsersInSuscription,
  postUser,
  postUserInOrganization,
  postUsersInRole,
  putUserById,
  putUserPassword,
} from "../../api/broker";
import { SingleUserControlContextProps } from "../../interfaces/SuperAdmin/ContextProps/iSingleUserControl";
import {
  CognitoUser,
  PutUserPassword,
} from "../../interfaces/SuperAdmin/Others/CognitoUser";
import { DynamoUser } from "../../interfaces/SuperAdmin/Others/DynamoUser";
import { Estado } from "../../interfaces/SuperAdmin/Others/iDataEstado";
import { iDataPaises } from "../../interfaces/SuperAdmin/Others/iDataPaises";
import {
  iDataUser,
  PostUserFields,
  PutUserFields,
} from "../../interfaces/SuperAdmin/Pages/iDataUser";
import { iDataUserSuscription } from "../../interfaces/SuperAdmin/Pages/iDataUserSuscription";
import { iDataUserRoles } from "../../interfaces/SuperAdmin/Pages/iDataUserRoles";
import { iDataUserOrganization } from "../../interfaces/SuperAdmin/Pages/iDataUserOrganization";
import { iDataUserSuborg } from "../../interfaces/SuperAdmin/Pages/iDataUserSuborg";
import { iDataOrganizacion } from "../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { AttachProps } from "../../components/Table/UserControl/AttachModal";

const SingleUserControlContext = createContext<SingleUserControlContextProps>(
  {} as SingleUserControlContextProps
);

interface Props {
  children: ReactNode;
}

const SingleUserControlProvider: FC<Props> = ({ children }) => {
  const { idUser } = useParams();

  const [rdsUser, setRdsUser] = useState<iDataUser | null>(null);
  const [cognitoCredentialsUser, setCognitoCredentialsUser] = useState<
    CognitoUser | undefined
  >(undefined);
  const [cognitoProviderUser, setCognitoProviderUser] = useState<
    CognitoUser | undefined
  >(undefined);
  const [dynamoUser, setDynamoUser] = useState<DynamoUser | null>(null);
  const [paises, setPaises] = useState<iDataPaises[]>([]);
  const [suscriptions, setSuscriptions] = useState<iDataUserSuscription[]>([]);
  const [myRoles, setMyRoles] = useState<iDataUserRoles[]>([]);
  const [myOrgs, setMyOrgs] = useState<iDataUserOrganization[]>([]);
  const [mySuborgs, setMySuborgs] = useState<iDataUserSuborg[]>([]);
  const [allOrgs, setAllOrgs] = useState<iDataOrganizacion[]>([]);

  // Con este estado se chequerá si se puede usar o no el botón 'Activar usuario'
  const [isUserActive, setIsUserActive] = useState(false);

  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingOtherUser, setIsLoadingOtherUser] = useState(true);
  const [isLoadingPaises, setIsLoadingPaises] = useState(true);
  const [isLoadingSus, setIsLoadingSus] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);

  const [isActivating, setIsActivating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [isDeletingAdmin, setIsDeletingAdmin] = useState(false);

  //Funcion para traer usuario de RDS
  const fetchRdsUser = async (id: string) => {
    setIsLoadingUser(true);

    try {
      const userRes = await getUserById(id);
      console.log(userRes);

      if (userRes.data.statusCode === 200) {
        setRdsUser(userRes.data.body); // Asignamos el usuario
      } else {
        toast.error("Error al obtener el usuario de RDS");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener el usuario de RDS");
    } finally {
      setIsLoadingUser(false);
    }
  };

  // Función para traer usuarios de Cognito y DynamoDB en base al email
  const fetchOtherUsers = async (email: string, version: string) => {
    setIsLoadingOtherUser(true);

    try {
      const [cognitoRes, dynamoRes] = await Promise.all([
        getCognitoUsers(version, email),
        getDynamoUsers(version, email),
      ]);

      console.log(cognitoRes);
      console.log(dynamoRes);

      if (cognitoRes.data.statusCode === 200) {
        // Usuario con credenciales
        const credentials = cognitoRes.data.body.find(
          (user) => user.status !== "EXTERNAL_PROVIDER"
        );
        setCognitoCredentialsUser(credentials);

        // Usuario con proveedor
        const provider = cognitoRes.data.body.find(
          (user) => user.status === "EXTERNAL_PROVIDER"
        );
        setCognitoProviderUser(provider);
      } else {
        toast.error("Error al obtener usuarios de Cognito");
      }

      if (dynamoRes.data.statusCode === 200) {
        setDynamoUser(dynamoRes.data.body[0]); // Asignamos el usuario
      } else {
        toast.error("Error al obtener usuario de DynamoDB");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener usuarios de Cognito y DynamoDB");
    } finally {
      setIsLoadingOtherUser(false);
    }
  };

  // Fetch de paises
  const fetchPaises = async () => {
    setIsLoadingPaises(true);
    try {
      const res = await getAllPaises();

      if (res.data.statusCode === 200) {
        setPaises(res.data.body);
      } else {
        toast.error("Error al obtener los paises");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los paises");
    } finally {
      setIsLoadingPaises(false);
    }
  };

  // Función para traer las suscripciones, roles y organización
  const fetchSuscriptions = async (id: string) => {
    setIsLoadingSus(true);
    try {
      //Fetch del plan y todas las suscripciones
      const [susRes, rolesRes, orgRes, suborgRes] = await Promise.all([
        getUsersInSuscription(undefined, id),
        getUsersInRole(undefined, id),
        getUsersByOrgId(undefined, id),
        getUsersInSuborg(undefined, id),
      ]);

      console.log(susRes);
      console.log(rolesRes);
      console.log(orgRes);
      console.log(suborgRes);
      if (susRes.data.statusCode === 200) {
        setSuscriptions(susRes.data.body); //Asignamos las suscripciones
      } else {
        toast.error("Error al obtener sus suscripciones");
      }

      if (rolesRes.data.statusCode === 200) {
        setMyRoles(rolesRes.data.body);
      } else {
        toast.error("Error al obtener roles");
      }

      if (orgRes.data.statusCode === 200) {
        setMyOrgs(orgRes.data.body);
      } else {
        toast.error("Error al obtener la organización");
      }

      if (suborgRes.data.statusCode === 200) {
        setMySuborgs(suborgRes.data.body);
      } else {
        toast.error("Error al obtener la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener sus suscripciones, roles y/o organización");
    } finally {
      setIsLoadingSus(false);
    }
  };

  // Funcion para activar usuario
  const userActivate = async () => {
    if (rdsUser) {
      // Creamos el objeto user con los valores anteriores
      const user: PostUserFields = {
        nombres: rdsUser.nombres,
        apellidos: rdsUser.apellidos,
        email: rdsUser.email,
        version: rdsUser.version,
        pais: rdsUser.pais,
      };
      setIsActivating(true);
      try {
        //Creamos el usuario
        const userRes = await postUser(user);
        const { statusCode, body } = userRes.data;
        console.log(userRes);

        if (statusCode === 200) {
          toast.success("Usuario activado en las distintas DBs");

          // Asignamos los usuarios
          setRdsUser(body.rds);
          setCognitoCredentialsUser(
            body.credentials
              ? { ...body.credentials, enabled: true }
              : undefined
          );
          setCognitoProviderUser(body.google as CognitoUser | undefined);
          setDynamoUser(body.dynamo);
        } else {
          toast.error("Error al registrar el usuario en las DB");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al registrar el usuario en las DB");
      } finally {
        setIsActivating(false);
      }
    }
  };

  // Función para editar el usuario
  const userEdit = async (id_usuario: string, fields: PutUserFields) => {
    setIsEditing(true);
    try {
      const { data } = await putUserById(id_usuario, fields);
      console.log(data);
      if (data.statusCode === 200) {
        setRdsUser(data.body);
        toast.success("Usuario editado exitosamente");
        const { body } = data;

        // Actualizamos DynamoUser si existe
        if (dynamoUser) {
          setDynamoUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              name: `${body.nombres?.trim()} ${body.apellidos?.trim()}`,
              updatedAt: new Date().toISOString(),
              isActive: body.estado === Estado.ACTIVO,
            };
          });
        }

        const newCognito = {
          enabled: body.estado === Estado.ACTIVO,
          updated_at: new Date().toISOString(),
        };

        //Actualizamos credenciales si existe
        if (cognitoCredentialsUser) {
          setCognitoCredentialsUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...newCognito,
            };
          });
        }

        //Actualizamos credenciales si existe
        if (cognitoProviderUser) {
          setCognitoProviderUser((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...newCognito,
            };
          });
        }
      } else {
        toast.error("Error al editar usuario");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar usuario");
    } finally {
      setIsEditing(false);
    }
  };

  // Funcion para cambiar contraseña
  const changePassword = async (username: string, data: PutUserPassword) => {
    if (cognitoCredentialsUser) {
      setIsChangingPass(true);
      try {
        // Cambiamos la contraseña
        const res = await putUserPassword(username, data);
        console.log(res);

        if (res.data.statusCode === 200) {
          toast.success("Cambio de contraseña en Cognito exitoso");
        } else {
          toast.error("Error al cambiar contraseña en Cognito");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al cambiar contraseña en Cognito");
      } finally {
        setIsChangingPass(false);
      }
    }
  };

  // Función para desanexar usuario de una organización
  const detachUser = async () => {
    const id_usuario = rdsUser?.id_usuario;
    const id_organizacion = myOrgs[0].id_organizacion;
    // Solo ejecutamos la función en caso de que exista el idUser y tenga organizaciones
    if (id_usuario && id_organizacion) {
      setIsDetaching(true);
      try {
        const { data } = await deleteUserInOrganization(
          id_usuario,
          id_organizacion
        );

        if (data.statusCode === 200) {
          setMyOrgs((prev) => {
            return prev.filter(
              (item) => item.id_organizacion !== id_organizacion
            );
          });
        } else {
          toast.error("Error al desanexar usuario de su organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al desanexar usuario de su organización");
      } finally {
        setIsDetaching(false);
      }
    }
  };

  // Función para anexar usuario a una organización
  const attachUser = async (data: AttachProps) => {
    setIsAttaching(true);
    try {
      const res = await postUserInOrganization(
        data.id_usuario,
        data.id_organizacion
      );

      if (res.data.statusCode === 201) {
        setMyOrgs((prev) => [...prev, res.data.body]);
      } else {
        toast.error("Error al anexar usuario a organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al al anexar usuario a organización");
    } finally {
      setIsAttaching(false);
    }
  };

  // Fetch de todas las organizaciones
  const fetchOrgs = async () => {
    if (rdsUser) {
      setIsLoadingOrgs(true);
      try {
        const res = await getAllOrganizations(rdsUser.version);
        console.log(res);

        if (res.data.statusCode === 200) {
          setAllOrgs(res.data.body);
        } else {
          toast.error("Error al obtener las organizaciones");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener las organizaciones");
      } finally {
        setIsLoadingOrgs(false);
      }
    }
  };

  // Función para crear Admin de Dominio
  const createAdminDeDominio = async () => {
    const id_usuario = rdsUser?.id_usuario;
    const version = rdsUser?.version;
    if (id_usuario && version) {
      setIsCreatingAdmin(true);
      try {
        if (rdsUser.estado === Estado.ELIMINADO) {
          toast.error(
            "No se puede asignar Admin de Dominio a un usuario eliminado"
          );
          return;
        }
        const res = await postUsersInRole({
          id_rol: ADMINS_DE_DOMINIO[version],
          id_usuarios: [id_usuario],
        });
        console.log(res);

        if (res.data.statusCode === 201) {
          setMyRoles((prev) => [...prev, ...res.data.body]);
        } else {
          toast.error("Error al asignar Admin de Dominio al usuario");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al al asignar Admin de Dominio al usuario");
      } finally {
        setIsCreatingAdmin(false);
      }
    }
  };

  // Función para quitarle Admin de Dominio a alguien
  const deleteAdminDeDominio = async () => {
    const adminDeDominio = myRoles.find(
      (rol) => rol.rol === "Admin de Dominio"
    );
    if (adminDeDominio) {
      setIsDeletingAdmin(true);
      try {
        const res = await deleteUsersInRole({
          ids: [adminDeDominio.id_usuario_rol],
        });
        console.log(res);

        if (res.data.statusCode === 200) {
          setMyRoles((prev) => {
            return prev.filter((item) => item.id_rol !== adminDeDominio.id_rol);
          });
        } else {
          toast.error("Error al quitar Admin de Dominio al usuario");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al al quitar Admin de Dominio al usuario");
      } finally {
        setIsDeletingAdmin(false);
      }
    }
  };

  // Hacemos el fetch del usuario y sus suscripciones apenas entremos
  useEffect(() => {
    if (idUser) {
      fetchRdsUser(idUser);
    }
  }, [idUser]);

  // Hacemos el fetch de los usuarios en Cognito y DynamoDB
  useEffect(() => {
    const cognitoExist = !!(cognitoCredentialsUser || cognitoProviderUser);
    if (rdsUser) {
      fetchSuscriptions(idUser as string);
      if (!dynamoUser && !cognitoExist) {
        fetchOtherUsers(rdsUser.email, rdsUser.version);
      }
    }
  }, [rdsUser]);

  useEffect(() => {
    const anyCongitoUser = cognitoCredentialsUser || cognitoProviderUser;

    // Si no existe algun usuario de DynamoDB, RDS o alguno de los 2 de Cognito
    if (!dynamoUser || !anyCongitoUser || !rdsUser) {
      return setIsUserActive(false);
    }

    const isCognitoActive =
      cognitoCredentialsUser?.enabled || cognitoProviderUser?.enabled;

    // Si ya sea DynamoDB, RDS o alguno de Cognito que tengan su estado Inactivo
    if (
      !dynamoUser.isActive ||
      rdsUser.estado !== Estado.ACTIVO ||
      !isCognitoActive
    ) {
      return setIsUserActive(false);
    }

    setIsUserActive(true);
  }, [dynamoUser, cognitoCredentialsUser, cognitoProviderUser, rdsUser]);

  return (
    <SingleUserControlContext.Provider
      value={{
        rdsUser,
        cognitoCredentialsUser,
        cognitoProviderUser,
        dynamoUser,
        paises,
        myRoles,
        myOrgs,
        mySuborgs,
        allOrgs,
        suscriptions,
        isUserActive,
        isLoadingUser,
        isLoadingOtherUser,
        isLoadingPaises,
        isLoadingSus,
        isLoadingOrgs,
        isActivating,
        isEditing,
        isChangingPass,
        isDetaching,
        isAttaching,
        isCreatingAdmin,
        isDeletingAdmin,
        fetchPaises,
        userActivate,
        userEdit,
        changePassword,
        detachUser,
        fetchOrgs,
        attachUser,
        deleteAdminDeDominio,
        createAdminDeDominio,
      }}
    >
      {children}
    </SingleUserControlContext.Provider>
  );
};

const useSingleUserControl = () => useContext(SingleUserControlContext);

export { SingleUserControlProvider, useSingleUserControl };
